import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import type { ActionResult } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { CaprichoKDS } from '../../components/admin/CaprichoKDS';
import { MenuToggleDrawer } from '../../components/admin/MenuToggleDrawer';
import { Clock, Check, AlertCircle, CreditCard, Banknote, Smartphone, Play, ChefHat, Truck, XCircle, X, Eye, Search, UtensilsCrossed } from 'lucide-react';
import { KDSOrderDetailModal } from '../../components/admin/KDSOrderDetailModal';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
// ─── Alert sound for completed orders ────────────────────────
const playCompletionSound = () => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // Play a pleasant two-tone chime
    const playTone = (freq: number, startTime: number, duration: number) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = audioCtx.currentTime;
    playTone(880, now, 0.15); // A5
    playTone(1108, now + 0.15, 0.15); // C#6
    playTone(1320, now + 0.3, 0.3); // E6
  } catch (e) {
    console.warn('[KDS] Could not play sound:', e);
  }
};
// Global roles that can manage the full order lifecycle
const GLOBAL_ROLES = ['owner', 'admin', 'manager', 'caja', 'super_admin', 'platform_admin'];
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function KDSPage() {
  const slug = useTenantSlug();
  const [searchParams] = useSearchParams();
  const lockedStation = searchParams.get('station');
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][KDS] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][KDS] No restaurant loaded — cannot toggle');
      addToast('error', 'No se pudo cargar el restaurante');
      return;
    }
    setTogglingKitchen(true);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][KDS] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      addToast('success', result.newValue ? '⏸ Cocina pausada — no se reciben pedidos' : '▶ Cocina activa — recibiendo pedidos');
    } else {
      setKitchenPaused(result.newValue);
      addToast('error', result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][KDS] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemReady,
    markStationItemsReady,
    refetchOrders,
    setOrders
  } = useAdminOrders(restaurant?.id);
  const [activeStation, setActiveStation] = useState<string>('all');
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [detailOrder, setDetailOrder] = useState<any>(null);
  const [deliveryJobsData, setDeliveryJobsData] = useState<Record<string, any>>({});
  const completedOrdersRef = useRef<Set<string>>(new Set());
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isNoiza = String(restaurant?.id) === '1' || restaurant?.slug === 'noiza';
  // ─── Fetch Delivery Jobs for active orders ───────────────────
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    const fetchDeliveryJobs = async () => {
      const deliveryOrderIds = orders.filter((o: any) => o.delivery_type === 'delivery' && ['searching_driver', 'ready', 'out_for_delivery'].includes(o.status)).map((o: any) => o.id);
      if (deliveryOrderIds.length === 0) return;
      try {
        const {
          data: jobs
        } = await supabase.from('delivery_jobs').select('id, order_id, status, driver_id, created_at').in('order_id', deliveryOrderIds);
        if (!jobs) return;
        const driverIds = jobs.filter(j => j.driver_id).map(j => j.driver_id);
        let driversMap: Record<number, string> = {};
        if (driverIds.length > 0) {
          const {
            data: drivers
          } = await supabase.from('drivers').select('id, full_name').in('id', driverIds);
          if (drivers) {
            drivers.forEach(d => {
              driversMap[d.id] = d.full_name;
            });
          }
        }
        const newJobsData: Record<string, any> = {};
        jobs.forEach(job => {
          const searchingMinutes = Math.floor((new Date().getTime() - new Date(job.created_at).getTime()) / 60000);
          newJobsData[job.order_id] = {
            ...job,
            driverName: job.driver_id ? driversMap[job.driver_id] : null,
            searchingMinutes: searchingMinutes > 0 ? searchingMinutes : 0
          };
        });
        setDeliveryJobsData(newJobsData);
      } catch (err) {
        console.error('[KDS] Error fetching delivery jobs:', err);
      }
    };
    fetchDeliveryJobs();
    const interval = setInterval(fetchDeliveryJobs, 10000);
    return () => clearInterval(interval);
  }, [orders]);
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const userRole = tenantCtx?.role || 'owner';
  const isGlobalRole = GLOBAL_ROLES.includes(userRole);
  const isGlobalStation = (code: string) => code === 'all' || code === 'caja';
  const isLockedToProduction = lockedStation != null && !isGlobalStation(lockedStation);
  useEffect(() => {
    if (lockedStation) {
      const resolved = lockedStation === 'caja' ? 'all' : lockedStation;
      console.log('[NOIZA][KDS] === STATION DEBUG ===');
      console.log('[NOIZA][KDS] URL station param:', lockedStation);
      console.log('[NOIZA][KDS] Resolved activeStation:', resolved);
      console.log('[NOIZA][KDS] isGlobalStation:', isGlobalStation(resolved));
      console.log('[NOIZA][KDS] isLockedToProduction:', !isGlobalStation(lockedStation));
      setActiveStation(resolved);
    }
  }, [lockedStation]);
  // ─── Detect when ALL items in an order become ready → play sound ───
  // Must be before conditional returns to respect Rules of Hooks
  const activeOrdersForSound = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  useEffect(() => {
    if (!orders.length) return;
    const newlyCompleted: string[] = [];
    for (const order of activeOrdersForSound) {
      if (order.status !== 'preparing') continue;
      const allReady = order.items.length > 0 && order.items.every((i: any) => i.status === 'ready' || i.status === 'served');
      if (allReady && !completedOrdersRef.current.has(order.id)) {
        newlyCompleted.push(order.id);
      }
    }
    if (newlyCompleted.length > 0) {
      newlyCompleted.forEach(id => completedOrdersRef.current.add(id));
      playCompletionSound();
      newlyCompleted.forEach(id => {
        const o = activeOrdersForSound.find((ord: any) => ord.id === id);
        if (o) {
          const parts = o.order_number.split('-');
          const short = parts[parts.length - 1] || o.order_number;
          addToast('success', `🎉 Pedido #${short} completado`);
        }
      });
      console.log('[KDS] 🔔 All items ready for orders:', newlyCompleted);
    }
  }, [orders]);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-2923">
        <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-2924" />
      </div>;
  }
  if (isCapricho) {
    return <AdminLayout restaurant={restaurant} role={userRole} onSignOut={tenantCtx?.signOut} data-id="element-2925">
        <CaprichoKDS restaurant={restaurant} data-id="element-2926" />
      </AdminLayout>;
  }
  // Show active orders (not delivered/cancelled)
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  // DEBUG: Log station filtering details (comprehensive)
  if (!isGlobalStation(activeStation) && activeOrders.length > 0) {
    console.log('[NOIZA][KDS] ═══════════════════════════════════');
    console.log('[NOIZA][KDS] STATION FILTER DEBUG');
    console.log('[NOIZA][KDS] activeStation:', activeStation);
    console.log('[NOIZA][KDS] Total active orders:', activeOrders.length);
    activeOrders.forEach((order: any) => {
      const matchingItems = order.items.filter((i: any) => i.station_code === activeStation);
      console.log(`[NOIZA][KDS] Order #${order.order_number} (id: ${order.id}, status: ${order.status})`);
      order.items.forEach((i: any) => {
        const match = i.station_code === activeStation ? '✅' : '❌';
        console.log(`  ${match} ${i.item_name_snapshot} — station_code: "${i.station_code}" (status: ${i.status})`);
      });
      console.log(`  → ${matchingItems.length}/${order.items.length} items match "${activeStation}"`);
    });
    console.log('[NOIZA][KDS] ═══════════════════════════════════');
  }
  const displayOrders = activeOrders.filter((order: any) => {
    if (isGlobalStation(activeStation)) return true;
    // Station views (cocina, pizzeria, bebidas) only see orders that have been
    // accepted by caja — pending/new orders are hidden from production stations
    if (order.status === 'new') return false;
    // In station view, check if there are any NON-ready items for this station
    const stationItems = order.items.filter((item: any) => item.station_code === activeStation);
    const pendingStationItems = stationItems.filter((item: any) => item.status !== 'ready' && item.status !== 'served');
    // Hide order from station if all its items for this station are done
    return pendingStationItems.length > 0;
  });
  // DEBUG: Log final display result
  if (!isGlobalStation(activeStation)) {
    console.log('[NOIZA][KDS] displayOrders for station', activeStation, ':', displayOrders.length);
  }
  const getWaitTimeColor = (createdAt: string) => {
    const minutes = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / 60000);
    if (minutes > 20) return 'text-red-500 bg-red-50 border-red-200';
    if (minutes > 10) return 'text-amber-500 bg-amber-50 border-amber-200';
    return 'text-emerald-500 bg-emerald-50 border-emerald-200';
  };
  // ─── Order-level action handler ────────────────────────────
  const handleOrderAction = async (orderId: string, action: string) => {
    // DEBUG: Verify correct ID usage
    const targetOrder = orders.find((o: any) => o.id === orderId);
    const shortDisplay = targetOrder ? getShortOrderNumber(targetOrder.order_number) : '?';
    console.log('[NOIZA][KDS] === ORDER ACTION DEBUG ===');
    console.log('[NOIZA][KDS] order.id (real PK):', orderId);
    console.log('[NOIZA][KDS] order.order_number:', targetOrder?.order_number);
    console.log('[NOIZA][KDS] short display #:', shortDisplay);
    console.log('[NOIZA][KDS] action:', action);
    console.log('[NOIZA][KDS] update payload will use .eq("id",', orderId, ')');
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'accept':
          result = await acceptOrder(orderId);
          if (result.success) addToast('success', '✓ Pedido aceptado');
          break;
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Entregado');
          break;
        case 'cancel':
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            result = await cancelOrder(orderId);
            if (result.success) addToast('success', '❌ Cancelado');
          } else {
            result = {
              success: true
            };
          }
          break;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // ─── Mark single item ready (with RPC + fallback) ────────────
  const markItemReadyWithFallback = async (orderItemId: number, rpcName: string) => {
    console.log(`🔥 KDS item clicked (${rpcName})`, orderItemId);
    const itemIdStr = String(orderItemId);
    setUpdatingItems(prev => new Set(prev).add(itemIdStr));
    // Optimistic update — immediately mark item as ready in local state
    setOrders((prev: any[]) => prev.map((order: any) => ({
      ...order,
      items: order.items.map((item: any) => String(item.id) === itemIdStr ? {
        ...item,
        status: 'ready'
      } : item)
    })));
    try {
      // Step 1: Try RPC
      const {
        data,
        error
      } = await supabase.rpc(rpcName, {
        p_order_item_id: orderItemId
      });
      let updated = false;
      if (error) {
        console.warn(`🔥 RPC ${rpcName} failed:`, error.message, '— using direct update fallback');
      } else {
        updated = true;
      }
      // Step 2: Fallback — direct update if RPC failed
      if (!updated) {
        console.log('🔥 Fallback: direct order_items update for id:', orderItemId);
        const {
          error: updateError,
          count
        } = await supabase.from('order_items').update({
          status: 'ready'
        }).eq('id', orderItemId);
        if (updateError) {
          console.error('🔥 Fallback update also failed:', updateError);
          addToast('error', `Error: ${updateError.message}`);
          // Revert optimistic update
          await refetchOrders();
          return;
        }
        console.log('🔥 Fallback update succeeded');
      }
      // Step 3: Refetch and verify
      await refetchOrders();
      addToast('success', '✅ Item listo');
    } catch (err: any) {
      console.error('🔥 EXCEPTION:', err);
      addToast('error', err.message);
      // Revert optimistic update on error
      await refetchOrders();
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemIdStr);
        return next;
      });
    }
  };
  const handleNoizaMarkReady = (orderItemId: number) => markItemReadyWithFallback(orderItemId, 'noiza_kds_mark_item_ready');
  const handleMarkReady = (orderItemId: number) => markItemReadyWithFallback(orderItemId, 'kds_mark_item_ready');
  // ─── Station-level action handler ──────────────────────────
  const handleStationAction = async (orderId: string, stationCode: string) => {
    console.log('[NOIZA][KDS] === STATION ACTION DEBUG ===');
    console.log('[NOIZA][KDS] order.id:', orderId);
    console.log('[NOIZA][KDS] station_code:', stationCode);
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    // Optimistic update — immediately mark all station items as ready
    const isAllStations = stationCode === 'all' || stationCode === 'caja';
    setOrders((prev: any[]) => prev.map((order: any) => {
      if (order.id !== orderId) return order;
      return {
        ...order,
        items: order.items.map((item: any) => {
          if (item.status === 'ready' || item.status === 'served') return item;
          if (isAllStations || item.station_code === stationCode) {
            return {
              ...item,
              status: 'ready'
            };
          }
          return item;
        })
      };
    }));
    try {
      const result = await markStationItemsReady(orderId, stationCode);
      if (result.success) {
        addToast('success', '✅ Items de estación marcados listos');
      } else if (result.error) {
        addToast('error', result.error);
        // Revert optimistic update on failure
        await refetchOrders();
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al marcar items listos');
      await refetchOrders();
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // Header title
  let headerTitle = 'KDS — Todas las Estaciones';
  if (activeStation !== 'all') {
    if (isGlobalStation(activeStation)) {
      headerTitle = 'KDS — Caja (Vista Global)';
    } else {
      const stationName = stations.find(s => s.code === activeStation)?.name;
      if (stationName) headerTitle = `KDS — ${stationName}`;
    }
  }
  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'sinpe':
        return <Smartphone className="w-4 h-4" data-id="element-2927" />;
      case 'card':
        return <CreditCard className="w-4 h-4" data-id="element-2928" />;
      case 'cash':
        return <Banknote className="w-4 h-4" data-id="element-2929" />;
      default:
        return null;
    }
  };
  const getPaymentLabel = (method: string) => {
    switch (method) {
      case 'sinpe':
        return 'SINPE';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      default:
        return method;
    }
  };
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="info" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-2930">
            Nuevo
          </Badge>;
      case 'accepted':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-2931">
            Aceptado
          </Badge>;
      case 'preparing':
        return <Badge variant="default" className="bg-amber-50 text-amber-700 border-amber-200" data-id="element-2932">
            Preparando
          </Badge>;
      case 'ready':
        return <Badge variant="success" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-2933">
            Listo
          </Badge>;
      case 'searching_driver':
        return <Badge variant="default" className="bg-orange-50 text-orange-700 border-orange-200 animate-pulse" data-id="element-2934">
            🔍 Buscando conductor
          </Badge>;
      case 'out_for_delivery':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-2935">
            🚗 En camino
          </Badge>;
      case 'delivered':
        return <Badge variant="default" className="bg-gray-50 text-gray-700 border-gray-200" data-id="element-2936">
            ✅ Entregado
          </Badge>;
      default:
        return <Badge variant="default" data-id="element-2937">{status}</Badge>;
    }
  };
  const getItemStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'bg-gray-100 text-gray-600',
          actionLabel: 'En preparación',
          actionColor: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600'
        };
      case 'preparing':
        return {
          label: 'Preparando',
          color: 'bg-amber-100 text-amber-700',
          actionLabel: 'Marcar listo',
          actionColor: 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600'
        };
      case 'ready':
        return {
          label: 'Listo',
          color: 'bg-emerald-100 text-emerald-700',
          actionLabel: '',
          actionColor: ''
        };
      default:
        return {
          label: status,
          color: 'bg-gray-100 text-gray-600',
          actionLabel: '',
          actionColor: ''
        };
    }
  };
  // Helper to format short order number
  const getShortOrderNumber = (orderNumber: string) => {
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2938">
      <div className="min-h-screen bg-[#0B0B0B] p-2 sm:p-3 text-white" data-id="element-2939">
        {/* Header — ultra compact */}
        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-2940">
          <div className="flex items-center gap-2" data-id="element-2941">
            <h1 className="text-lg font-black text-white tracking-tight" data-id="element-2942">
              {headerTitle}
            </h1>
            <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded-full text-xs font-bold tabular-nums" data-id="element-2943">
              {displayOrders.length}
            </span>
          </div>

          <div className="flex items-center gap-2" data-id="element-2944">
            {/* Menu Toggle Button */}
            {isGlobalRole && <button onClick={() => setIsMenuDrawerOpen(true)} className="h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0 bg-white/10 text-white hover:bg-white/20 border border-white/5" data-id="element-2945">
                <UtensilsCrossed className="w-3 h-3" data-id="element-2946" />
                Menú
              </button>}

            {/* Kitchen Pause Toggle */}
            {isGlobalRole && <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0', kitchenPaused ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30', togglingKitchen && 'opacity-50 pointer-events-none')} data-id="element-2947">
                {kitchenPaused ? <>
                    <Play className="w-3 h-3" data-id="element-2948" />
                    Activar Cocina
                  </> : <>
                    <XCircle className="w-3 h-3" data-id="element-2949" />
                    Pausar Cocina
                  </>}
              </button>}
          </div>
        </div>

        {/* Station Tabs — compact pills */}
        {!isLockedToProduction && <div className="flex overflow-x-auto no-scrollbar gap-1.5" data-id="element-2950">
            <button onClick={() => setActiveStation('all')} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors active:scale-95', activeStation === 'all' ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-2951">
              Todos
            </button>
            {stations.map(station => <button key={station.id} onClick={() => setActiveStation(station.code)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors active:scale-95', activeStation === station.code ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-2952">
                {station.name}
              </button>)}
          </div>}
        {/* Orders Grid — high density: 2→3→4 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 items-start" data-id="element-2953">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-48 rounded-xl bg-[#141414] animate-pulse border border-white/5" data-id="element-2954" />) : displayOrders.length > 0 ? displayOrders.map((order: any) => {
          // Items that belong to this station (or all if global)
          const stationItems = order.items.filter((item: any) => isGlobalStation(activeStation) || item.station_code === activeStation);
          // In station view, only show non-ready items
          const visibleItems = isGlobalStation(activeStation) ? stationItems : stationItems.filter((item: any) => item.status !== 'ready' && item.status !== 'served');
          // DEBUG: Log filtering for each order
          if (!isGlobalStation(activeStation)) {
            console.log(`[NOIZA][KDS] Order #${order.order_number}:`);
            console.log(`  Total items: ${order.items.length}`);
            console.log(`  Station items for ${activeStation}: ${stationItems.length}`);
            console.log(`  Visible (pending) items: ${visibleItems.length}`);
          }
          // CRITICAL: Skip rendering if no pending items for this station
          if (visibleItems.length === 0) {
            return null;
          }
          const readyItemsCount = visibleItems.filter((i: any) => i.status === 'ready' || i.status === 'served').length;
          const allItemsReady = visibleItems.length > 0 && readyItemsCount === visibleItems.length;
          // "FALTAN..." only when there's partial preparation:
          // at least one item is ready but not all are ready
          const isPartiallyReady = readyItemsCount > 0 && !allItemsReady;
          const isUpdatingOrder = updatingOrders.has(order.id);
          const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          let borderColor = 'border-white/10';
          let cardGlow = '';
          if (order.status === 'new') {
            borderColor = 'border-blue-500';
            cardGlow = 'kds-new-order';
          } else if (order.status === 'accepted') {
            borderColor = 'border-purple-500';
          } else if (order.status === 'preparing') {
            borderColor = 'border-amber-500';
          } else if (order.status === 'searching_driver') {
            borderColor = 'border-orange-500';
          } else if (order.status === 'out_for_delivery') {
            borderColor = 'border-cyan-500';
          } else if (allItemsReady) {
            borderColor = 'border-emerald-500';
          }
          return <div key={order.id} className={cn('bg-[#141414] rounded-xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden', borderColor, cardGlow)} data-id="element-2955">
                  {/* Header — single dense row */}
                  <div className="px-2.5 py-2 border-b border-white/5 flex items-center justify-between gap-1" data-id="element-2956">
                    <div className="flex items-center gap-1.5 min-w-0" data-id="element-2957">
                      <span className="text-sm font-black text-white tabular-nums" data-id="element-2958">
                        #{getShortOrderNumber(order.order_number)}
                      </span>
                      {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-2959">
                          🛵
                        </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-2960">
                          🏠
                        </span> : <span className="bg-white/10 text-white/50 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-2961">
                          🍽️{order.table_id || ''}
                        </span>}
                      <span className="text-white/30" data-id="element-2962">
                        {getPaymentIcon(order.payment_method)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0" data-id="element-2963">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDetailOrder(order);
                }} title="Ver detalle completo" className="w-7 h-7 rounded-lg flex items-center justify-center text-white/20 hover:text-white/60 hover:bg-white/10 transition-colors" data-id="element-2964">
                        <Eye className="w-4 h-4" data-id="element-2965" />
                      </button>
                      <span className={cn('text-lg font-black tabular-nums tracking-tighter', waitMinutes >= 20 ? 'kds-late-timer' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-2966">
                        {waitMinutes}m
                      </span>
                    </div>
                  </div>

                  {/* Customer — tiny */}
                  <div className="px-2.5 py-1 border-b border-white/5" data-id="element-2967">
                    <p className="text-xs text-white/50 font-medium truncate" data-id="element-2968">
                      {order.customer_name}
                    </p>
                  </div>

                  {/* Delivery Status Banner */}
                  {order.delivery_type === 'delivery' && deliveryJobsData[order.id] && <div className="border-b border-white/5" data-id="element-2969">
                        {order.status === 'searching_driver' && <div className="px-2.5 py-1.5 bg-orange-500/10 flex items-center gap-1.5" data-id="element-2970">
                            <Search className="w-3 h-3 text-orange-400 animate-pulse" data-id="element-2971" />
                            <p className="text-[10px] text-orange-400 font-bold leading-tight" data-id="element-2972">
                              Buscando conductor —{' '}
                              {deliveryJobsData[order.id].searchingMinutes}m
                            </p>
                          </div>}
                        {order.status === 'ready' && deliveryJobsData[order.id].driverName && <div className="px-2.5 py-1.5 bg-emerald-500/10 flex items-center gap-1.5" data-id="element-2973">
                              <Check className="w-3 h-3 text-emerald-400" data-id="element-2974" />
                              <p className="text-[10px] text-emerald-400 font-bold leading-tight truncate" data-id="element-2975">
                                Conductor asignado:{' '}
                                {deliveryJobsData[order.id].driverName} — en
                                camino al restaurante
                              </p>
                            </div>}
                        {order.status === 'out_for_delivery' && deliveryJobsData[order.id].driverName && <div className="px-2.5 py-1.5 bg-cyan-500/10 flex items-center gap-1.5" data-id="element-2976">
                              <Truck className="w-3 h-3 text-cyan-400" data-id="element-2977" />
                              <p className="text-[10px] text-cyan-400 font-bold leading-tight truncate" data-id="element-2978">
                                Conductor en camino:{' '}
                                {deliveryJobsData[order.id].driverName}
                              </p>
                            </div>}
                      </div>}

                  {/* Notes */}
                  {order.notes && <div className="px-2.5 py-1.5 bg-red-500/10 border-b border-red-500/20" data-id="element-2979">
                      <p className="text-[11px] text-red-400 font-bold leading-tight truncate" data-id="element-2980">
                        ⚠ {order.notes}
                      </p>
                    </div>}

                  {/* Items — compact checklist */}
                  <div className="flex-1 overflow-y-auto max-h-[200px]" data-id="element-2981">
                    {visibleItems.map((item: any) => {
                const isItemReady = item.status === 'ready' || item.status === 'served';
                const isItemPreparing = item.status === 'preparing';
                const isUpdatingItem = updatingItems.has(item.id);
                return <div key={item.id} onClick={() => {
                  if (isUpdatingItem || isItemReady) return;
                  if (isNoiza) {
                    handleNoizaMarkReady(item.id);
                  } else {
                    handleMarkReady(item.id);
                  }
                }} className={cn('flex items-center gap-2 px-2.5 py-1.5 border-b border-white/5 transition-all', isItemReady ? 'opacity-30 cursor-default' : isItemPreparing ? 'bg-amber-500/10 cursor-pointer select-none hover:bg-amber-500/20 active:scale-[0.98] border-l-2 border-l-amber-500' : 'cursor-pointer select-none hover:bg-white/10 active:scale-[0.98] border-l-2 border-l-white/20 hover:border-l-amber-400')} data-id="element-2982">
                          {/* Qty */}
                          <span className={cn('w-6 h-6 rounded-full flex items-center justify-center text-xs font-black shrink-0', isItemReady ? 'bg-emerald-500/20 text-emerald-400' : isItemPreparing ? 'bg-amber-500 text-black' : 'bg-white/15 text-white')} data-id="element-2983">
                            {item.quantity}
                          </span>

                          {/* Name + options inline */}
                          <div className="flex-1 min-w-0" data-id="element-2984">
                            <p className={cn('text-sm font-bold leading-tight truncate', isItemReady ? 'line-through text-white/40' : 'text-white')} data-id="element-2985">
                              {item.item_name_snapshot}
                            </p>
                            {item.options && item.options.length > 0 && <p className="text-[10px] text-white/40 truncate leading-tight" data-id="element-2986">
                                {item.options.map((o: any) => o.option_name).join(' · ')}
                              </p>}
                            {item.notes && <p className="text-[10px] font-black text-red-400 uppercase truncate" data-id="element-2987">
                                ** {item.notes}
                              </p>}
                          </div>

                          {/* Status indicator */}
                          <div className="shrink-0 flex items-center gap-1" data-id="element-2988">
                            {isUpdatingItem ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" data-id="element-2989" /> : isItemReady ? <Check className="w-5 h-5 text-emerald-500" data-id="element-2990" /> : <div className="flex items-center gap-1 bg-emerald-500/20 px-2 py-1 rounded-md" data-id="element-2991">
                                <Check className="w-4 h-4 text-emerald-400" data-id="element-2992" />
                                <span className="text-[10px] font-black text-emerald-400" data-id="element-2993">
                                  LISTO
                                </span>
                              </div>}
                          </div>
                        </div>;
              })}
                  </div>

                  {/* Action — compact but tappable */}
                  <div className="p-2 border-t border-white/5" data-id="element-2994">
                    {isGlobalRole && isGlobalStation(activeStation) ? <>
                        {order.status === 'new' && <button onClick={() => handleOrderAction(order.id, 'accept')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2995">
                            {isUpdatingOrder ? '...' : '✓ ACEPTAR'}
                          </button>}
                        {order.status === 'accepted' && <button onClick={() => handleOrderAction(order.id, 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2996">
                            {isUpdatingOrder ? '...' : '🔥 COCINAR'}
                          </button>}
                        {order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-10 rounded-lg text-sm font-black flex items-center justify-center gap-1 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95' : isPartiallyReady ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20 cursor-not-allowed' : 'bg-white/5 text-white/20 cursor-not-allowed')} data-id="element-2997">
                            {isUpdatingOrder ? '...' : allItemsReady ? '✅ LISTO' : isPartiallyReady ? `FALTAN ${visibleItems.length - readyItemsCount} de ${visibleItems.length}` : `🍳 ${readyItemsCount}/${visibleItems.length} listos`}
                          </button>}
                        {order.status === 'ready' && order.delivery_type === 'delivery' && <div className="h-10 flex items-center justify-center bg-orange-500/10 border border-orange-500/20 rounded-lg gap-2 animate-pulse" data-id="element-2998">
                              <span className="text-orange-400 font-black text-sm" data-id="element-2999">
                                🔍 Esperando delivery
                              </span>
                            </div>}
                        {order.status === 'ready' && order.delivery_type !== 'delivery' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3000">
                              {isUpdatingOrder ? '...' : '🚀 ENTREGAR'}
                            </button>}
                        {order.status === 'searching_driver' && <div className="h-10 flex items-center justify-center bg-orange-500/10 border border-orange-500/20 rounded-lg gap-2 animate-pulse" data-id="element-3001">
                            <span className="text-orange-400 font-black text-sm" data-id="element-3002">
                              🔍 Buscando conductor
                            </span>
                          </div>}
                        {order.status === 'out_for_delivery' && <div className="h-10 flex items-center justify-center bg-cyan-500/10 border border-cyan-500/20 rounded-lg gap-2" data-id="element-3003">
                            <span className="text-cyan-400 font-black text-sm" data-id="element-3004">
                              🚗 Conductor en camino
                            </span>
                          </div>}
                        {order.status === 'delivered' && <div className="h-10 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-lg gap-2" data-id="element-3005">
                            <span className="text-emerald-400 font-black text-sm" data-id="element-3006">
                              ✅ Entregado
                            </span>
                          </div>}
                        {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] text-white/15 hover:text-red-500 transition-colors mt-1" data-id="element-3007">
                            cancelar
                          </button>}
                      </> : <div className="space-y-2" data-id="element-3008">
                        {allItemsReady ? <div className="h-10 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-3009">
                            <p className="text-emerald-500 font-black text-sm" data-id="element-3010">
                              ✅ TODOS LISTOS
                            </p>
                          </div> : <button onClick={() => handleStationAction(order.id, activeStation)} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-emerald-500 hover:bg-emerald-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3011">
                            {isUpdatingOrder ? <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" data-id="element-3012" /> : <>
                                <Check className="w-5 h-5" data-id="element-3013" />
                                MARCAR {visibleItems.length - readyItemsCount}{' '}
                                LISTO
                                {visibleItems.length - readyItemsCount !== 1 ? 'S' : ''}
                              </>}
                          </button>}
                      </div>}
                  </div>
                </div>;
        }) : <div className="col-span-full py-20 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-xl border border-white/5 border-dashed" data-id="element-3014">
              <ChefHatIcon className="w-16 h-16 text-white/10 mb-4" data-id="element-3015" />
              <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-3016">
                🎉 ¡Todo al día!
              </h3>
              <p className="text-sm" data-id="element-3017">Sin pedidos pendientes.</p>
            </div>}
        </div>

        {/* ═══ ORDER DETAIL MODAL ═══ */}
        {detailOrder && <KDSOrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} onAction={handleOrderAction} isUpdating={updatingOrders.has(detailOrder.id)} data-id="element-3018" />}

        <MenuToggleDrawer restaurantId={restaurant.id} isOpen={isMenuDrawerOpen} onClose={() => setIsMenuDrawerOpen(false)} data-id="element-3019" />

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-3020">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-2xl border text-sm font-bold min-w-[240px] max-w-[360px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-3021">
                <span className="flex-1" data-id="element-3022">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-3023">
                  <X className="w-3.5 h-3.5" data-id="element-3024" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3025">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-3026" />
      <path d="M6 17h12" data-id="element-3027" />
    </svg>;
}